// The questions the PCP initial assessment asks that LOINC has no exact term
// for, with their answer sets and how each relates to LOINC. Questions LOINC
// names exactly (code status, FRAX, last Pap, last eye exam, last tetanus
// immunization) are recorded under the LOINC code itself and are not here.
import type { ConceptMapGroupElement, ValueSet } from '@medplum/fhirtypes'
import {
  type AnswerEntry,
  answerConcepts,
  answerValueSet,
  type Capture,
  type TemplateQuestion,
  templateQuestionConcept,
  templateQuestionLoincMap,
  VALUE_SET_BASE,
} from '#src/terminology/define.ts'
import { ASSESSMENT_ANSWERS_SYSTEM } from '#src/terminology/types.ts'

const YES_NO: Capture = {
  kind: 'choice',
  answerSet: `${VALUE_SET_BASE}/yes-no`,
}
const TEXT: Capture = { kind: 'text' }
const DATE: Capture = { kind: 'date' }
const choice = (name: string, options?: { repeats?: boolean }): Capture => {
  const capture: Capture = {
    kind: 'choice',
    answerSet: `${VALUE_SET_BASE}/${name}`,
  }
  if (options?.repeats) {
    capture.repeats = true
  }
  return capture
}

const PCP_QUESTIONS: TemplateQuestion[] = [
  {
    code: 'other-participants-description',
    display: 'Other persons participating',
    definition: 'Other persons participating (name and relationship)',
    domain: 'overview',
    capture: TEXT,
    unmatched: 'Encounter participants, not an observation LOINC names',
  },
  {
    code: 'spoke-in-preferred-language',
    display: 'Spoke in preferred language',
    definition:
      "Did the clinician speak with the participant directly in the participant's preferred language?",
    domain: 'overview',
    capture: YES_NO,
    unmatched: 'LOINC has interpreter need (54588-9), not language concordance',
  },
  {
    code: 'interpreter-id',
    display: 'Interpreter ID number',
    definition: 'Translator or interpreter ID number',
    domain: 'overview',
    capture: TEXT,
    unmatched: 'Interpreter identifiers are not LOINC observations',
  },
  {
    code: 'interpreter-name',
    display: 'Interpreter name',
    definition: 'Name of the translator or interpreter',
    domain: 'overview',
    capture: TEXT,
    unmatched: 'Interpreter identifiers are not LOINC observations',
  },
  {
    code: 'preferred-language',
    display: 'Preferred language',
    definition: "The participant's preferred language for health care",
    domain: 'overview',
    capture: choice('preferred-language'),
    loinc: {
      code: '54899-0',
      equivalence: 'equivalent',
      comment:
        'Same question. Our answers are the languages Seen serves; LOINC lists Chinese rather than Mandarin and Cantonese.',
    },
  },
  {
    code: 'priority-of-care-domain',
    display: 'Priority of care',
    definition:
      'Participant priority of care or health goal (check all that apply)',
    domain: 'priorities',
    capture: choice('priority-of-care-domains', { repeats: true }),
    loinc: {
      code: '87528-6',
      equivalence: 'relatedto',
      comment:
        'Personal health goal answers are treatment aims (quality of life, function), not the PACE priority domains',
    },
  },
  {
    code: 'family-history-cancer',
    display: 'Family history of cancer',
    definition: 'Family history of cancer',
    domain: 'family-history',
    capture: YES_NO,
    loinc: {
      code: '21850-3',
      equivalence: 'relatedto',
      comment: 'LOINC records family history of cancer as a narrative',
    },
  },
  {
    code: 'family-history-cancer-details',
    display: 'Family history of cancer details',
    definition: 'Age of onset and type of cancer in the family member',
    domain: 'family-history',
    capture: TEXT,
    loinc: {
      code: '21850-3',
      equivalence: 'wider',
      comment: 'The narrative also covers which family member',
    },
  },
  {
    code: 'family-history-dementia',
    display: 'Family history of dementia',
    definition: 'Family history of dementia',
    domain: 'family-history',
    capture: YES_NO,
    unmatched: 'LOINC has no family history of dementia term',
  },
  {
    code: 'family-history-dementia-details',
    display: 'Family history of dementia details',
    definition: 'Age of onset and type of dementia in the family member',
    domain: 'family-history',
    capture: TEXT,
    unmatched: 'LOINC has no family history of dementia term',
  },
  {
    code: 'family-history-fragility-fracture',
    display: 'Family history of fragility fracture',
    definition: 'Family history of fragility fracture',
    domain: 'family-history',
    capture: YES_NO,
    unmatched:
      'LOINC 98135-7 is the patient’s own low impact fracture, not a family member’s',
  },
  {
    code: 'family-history-fragility-fracture-onset-age',
    display: 'Family fragility fracture age of onset',
    definition: 'Age of onset of fragility fracture in the family member',
    domain: 'family-history',
    capture: { kind: 'quantity', unit: 'a' },
    loinc: {
      code: '75273-3',
      equivalence: 'wider',
      comment: 'Age at event onset in a family member, for any event',
    },
  },
  {
    code: 'high-risk-substance-use',
    display: 'High risk behaviors',
    definition:
      'High risk behaviors (alcohol, tobacco, recreational substance use)',
    domain: 'social-history',
    capture: YES_NO,
    loinc: {
      code: '39244-9',
      equivalence: 'relatedto',
      comment: 'CPHS drug or alcohol use leaves out tobacco',
    },
  },
  {
    code: 'assessment-summary',
    display: 'Assessment summary',
    definition: 'One-line summary of the assessment',
    domain: 'assessment',
    capture: TEXT,
    unmatched: 'LOINC 51848-0 is a whole evaluation note, not a summary line',
  },
  {
    code: 'ed-or-hospital-visit-6mo',
    display: 'ED or hospital visit, past 6 months',
    definition: 'Emergency department or hospital visit in the past 6 months',
    domain: 'health-maintenance',
    capture: YES_NO,
    loinc: {
      code: '93679-9',
      equivalence: 'relatedto',
      comment: 'LOINC counts emergency department visits only',
    },
  },
  {
    code: 'ed-or-hospital-visit-details',
    display: 'ED or hospital visit details',
    definition: 'Details of emergency department or hospital visits',
    domain: 'health-maintenance',
    capture: TEXT,
    unmatched: 'No LOINC narrative for visit details',
  },
  {
    code: 'tobacco-use',
    display: 'Tobacco use',
    definition: 'Tobacco use',
    domain: 'health-maintenance',
    capture: YES_NO,
    loinc: {
      code: '72166-2',
      equivalence: 'relatedto',
      comment:
        'Tobacco smoking status grades smoking (current, former, never); ours asks any tobacco use',
    },
  },
  {
    code: 'hearing-concern',
    display: 'Hearing issues',
    definition: 'Hearing issues',
    domain: 'health-maintenance',
    capture: YES_NO,
    loinc: {
      code: '69856-3',
      equivalence: 'relatedto',
      comment:
        'The ACS disability item asks about deafness or serious difficulty',
    },
  },
  {
    code: 'tb-exposure',
    display: 'Prior TB exposure',
    definition: 'Prior exposure to tuberculosis',
    domain: 'tuberculosis',
    capture: YES_NO,
    unmatched: 'LOINC 39209-2 is a CPHS screen result, not exposure history',
  },
  {
    code: 'tb-treatment-history',
    display: 'Prior TB treatment',
    definition: 'Prior treatment for tuberculosis (date or year included)',
    domain: 'tuberculosis',
    capture: YES_NO,
    unmatched: 'LOINC 45242-5 is the treatment start date',
  },
  {
    code: 'tb-symptoms-6mo',
    display: 'TB symptoms, past 6 months',
    definition:
      'Cough, hemoptysis, fever or night sweats, or unexplained weight loss persisting more than 4 weeks in the past 6 months',
    domain: 'tuberculosis',
    capture: YES_NO,
    unmatched: 'LOINC has the single symptoms, not this TB symptom screen',
  },
  {
    code: 'medication-reconciliation-completed',
    display: 'Medication reconciliation completed',
    definition: 'Medication reconciliation completed',
    domain: 'medications',
    capture: YES_NO,
    unmatched: 'LOINC 80820-4 is the reconciliation form, a document',
  },
  {
    code: 'falls-6mo',
    display: 'Falls, past 6 months',
    definition: 'Falls in the past 6 months',
    domain: 'risk-assessment',
    capture: YES_NO,
    loinc: {
      code: '52552-7',
      equivalence: 'wider',
      comment: 'LOINC looks back 1 year',
    },
  },
  {
    code: 'fall-details',
    display: 'Fall details',
    definition: 'Fall details, including injuries sustained, treatment and DME',
    domain: 'risk-assessment',
    capture: TEXT,
    unmatched: 'LOINC 100258-3 is fall-related injury history only',
  },
  {
    code: 'memory-concern',
    display: 'Memory concerns',
    definition: 'Memory concerns',
    domain: 'risk-assessment',
    capture: YES_NO,
    loinc: {
      code: '89174-7',
      equivalence: 'relatedto',
      comment: 'ECog asks the participant about their own memory or thinking',
    },
  },
  {
    code: 'incontinence-type',
    display: 'Incontinence',
    definition: 'Incontinence: urinary, bowel, both or none',
    domain: 'risk-assessment',
    capture: choice('incontinence-type'),
    unmatched:
      'LOINC grades urinary and bowel incontinence separately (46553-4, 46587-2)',
  },
  {
    code: 'sleep-concern',
    display: 'Sleep concerns',
    definition: 'Sleep concerns',
    domain: 'risk-assessment',
    capture: YES_NO,
    unmatched: 'LOINC sleep items are instrument-specific (PROMIS, PhenX)',
  },
  {
    code: 'polst-completed-date',
    display: 'POLST completed date',
    definition: 'Date the POLST form was completed',
    domain: 'advance-care-planning',
    capture: DATE,
    unmatched: 'The POLST panel (100821-8) has no completion date item',
  },
]

export const PCP_QUESTION_CONCEPTS = PCP_QUESTIONS.map(templateQuestionConcept)

export const PCP_QUESTION_LOINC_MAP: ConceptMapGroupElement[] =
  PCP_QUESTIONS.map(templateQuestionLoincMap)

function answerSet(
  name: string,
  title: string,
  codes: AnswerEntry[]
): ValueSet {
  return answerValueSet(
    name,
    title,
    ASSESSMENT_ANSWERS_SYSTEM,
    codes.map(([code, display]) => ({ code, display }))
  )
}

export const PCP_ANSWER_SETS: ValueSet[] = [
  answerSet('preferred-language', 'Preferred language', [
    ['mandarin', 'Mandarin'],
    ['cantonese', 'Cantonese'],
    ['other-specify', 'Other'],
  ]),
  answerSet('priority-of-care-domains', 'Priorities of care', [
    ['priority-function-mobility', 'Function and Mobility'],
    ['priority-social-connection', 'Social Connection'],
    ['priority-life-enjoyment', 'Life Enjoyment'],
    ['priority-health-management', 'Health Management'],
  ]),
  answerSet('incontinence-type', 'Incontinence', [
    ['urinary-incontinence', 'Urinary'],
    ['bowel-incontinence', 'Bowel'],
    ['urinary-and-bowel-incontinence', 'Both'],
    ['no-incontinence', 'None'],
  ]),
]

// The answer codes the sets above introduce. Mandarin, Cantonese and Other
// are base answer codes. Clinic answers a base answer set that has no concept
// of its own.
export const PCP_ANSWER_CONCEPTS = answerConcepts(
  [
    ['clinic', 'Clinic'],
    ...PCP_ANSWER_SETS.flatMap((set) =>
      (set.compose?.include ?? []).flatMap((include) =>
        (include.concept ?? []).map(({ code, display }): AnswerEntry => [
          code,
          display ?? code,
        ])
      )
    ),
  ],
  {
    reused: new Set(['mandarin', 'cantonese', 'other-specify']),
    displays: 'first',
  }
)

export const PCP_ANSWER_LOINC_MAP: ConceptMapGroupElement[] = [
  {
    code: 'mandarin',
    target: [{ code: 'LA4168-6', equivalence: 'wider', comment: 'Chinese' }],
  },
  {
    code: 'cantonese',
    target: [{ code: 'LA4168-6', equivalence: 'wider', comment: 'Chinese' }],
  },
  {
    code: 'other-specify',
    target: [{ code: 'LA46-8', equivalence: 'equivalent' }],
  },
]
